import { Button, Checkbox, FormControl, InputAdornment, InputLabel, MenuItem, Select, Switch, Table, TableCell, TableHead, TableRow, TableContainer, TextField } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import featuresSAService from 'services/superadmin/featuresSAService';
import { FEATURE_UNIT } from 'data/consts';

interface PlanFormProps {
    plan: any;
    onSubmit: (data: any) => void;
}

export const PlanForm = ({ plan, onSubmit }: PlanFormProps) => {
    // Plan features
    const { data: features, isLoading: isLoadingFeatures, refetch: refetchFeatures } = useQuery({
        queryKey: ['features'],
        queryFn: async () => {
            const { data } = await featuresSAService.getAll();
            return data;
        },
        refetchOnWindowFocus: false,
    });


    const methods = useForm({
        defaultValues: {
            name: plan?.name || '',
            description: plan?.description || '',
            isActive: plan?.isActive || true,
            price: plan?.price || 0,
            currency: plan?.currency || 'KZT',
            billingPeriod: plan?.billingPeriod || 'month',
            trialDays: plan?.trialDays || 0,
            features: plan?.features || [],
        },
    });

    const handleRemoveFeature = (index: number) => {
        const features = methods.getValues('features') || [];
        features.splice(index, 1);
        methods.setValue('features', features, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }

    const handleAddFeature = (feature: any) => {
        const features = methods.getValues('features') || [];
        features.push(feature)
        methods.setValue('features', features, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }


    return (
        <div>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col gap-4 mt-6">
                    <div id="plan-info">
                        <h3 className="text-lg font-bold mb-4">Информация о тарифе</h3>
                    </div>
                    <TextField label="Название" {...methods.register('name', { required: true })} fullWidth required />
                    <TextField label="Описание" {...methods.register('description')} fullWidth />
                    <Switch {...methods.register('isActive')} checked={methods.watch('isActive')} />
                    <TextField
                        fullWidth
                        type='text'
                        value={methods.watch('price')}
                        onChange={(e) => methods.setValue('price', Number(e.target.value) || 0)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        style={{
                            marginRight: 0,
                            paddingRight: 0,
                        }}
                        InputProps={{
                            endAdornment: <InputAdornment position='end'>
                                <Select
                                    variant='standard'
                                    value={methods.watch('currency')}
                                    onChange={(e) => methods.setValue('currency', e.target.value)}
                                    disableUnderline
                                    sx={{
                                        minWidth: 70,
                                        '& .MuiSelect-select': {
                                            padding: '0 8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                >
                                    <MenuItem value="KZT">KZT</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                </Select>
                            </InputAdornment>
                        }}
                    />
                    <Controller control={methods.control} name="billingPeriod" render={({ field }) => (
                        <FormControl fullWidth>
                            <InputLabel>Период оплаты</InputLabel>
                            <Select label="Период оплаты" {...field} fullWidth>
                                <MenuItem value="month">Месяц</MenuItem>
                                <MenuItem value="year">Год</MenuItem>
                            </Select>
                        </FormControl>
                    )} />
                    <TextField label="Количество дней пробного периода" {...methods.register('trialDays')} fullWidth />
                    <div id="plan-features">
                        <h3 className="text-lg font-bold mb-4">Функционал тарифа</h3>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Функционал</TableCell>
                                        <TableCell>Лимит</TableCell>
                                        <TableCell>Единица измерения</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                {methods.getValues('features')?.map((feature: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <span>{features?.find((f: any) => f.id === feature.id)?.name}</span>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                label="Лимит (пустое значение - нет лимита)"
                                                {...methods.register(`features.${index}.PlanFeature.limit`)}
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <span>{features?.find((f: any) => f.id === feature.id)?.unit}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Button variant="outlined" color="error" onClick={() => handleRemoveFeature(index)}>Удалить</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        </TableContainer>
                    </div>
                    <Button type="submit" variant="contained" color="primary">Сохранить</Button>
                    <pre>{JSON.stringify(methods.watch(), null, 2)}</pre>
                </form>
            </FormProvider>

            {/* Список функционалов */}
            <section>
                <h3 className="text-lg font-bold mb-4">Список функционалов</h3>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Функционал</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        {features?.filter((f: any) => !methods.getValues('features')?.some((ff: any) => ff.id === f.id))?.map((feature: any) => (
                            <TableRow key={feature.id}>
                                <TableCell>{feature.name}</TableCell>
                                <TableCell>
                                    <Button variant="outlined" color="primary" onClick={() => handleAddFeature(feature)}>Добавить</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                </TableContainer>
            </section>
        </div>
    )
}
